import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Modal, Typography, Button, IconButton, Fade, Backdrop } from '@mui/material';
import CanvaSidebar, { type CanvaSidebarTab } from './CanvaSidebar';
import TopToolbar from './TopToolbar';
import ReportCanvas from './ReportCanvas';
import RightPropsPanel from './RightPropsPanel';
import type { WidgetType, WidgetItem, WidgetProperties, ReportPage, ReportGlobalSettings } from './types';
import type { Layout } from 'react-grid-layout';
import AddWidgetModal, { type AddWidgetPayload } from './AddWidgetModal';
import { AlertTriangle, X } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showSuccess, showError } from '../../utils/toast';
import { useClientLevel } from '../../hooks/useClientLevel';

const DEFAULT_GLOBAL_SETTINGS: ReportGlobalSettings = {
  reportTitle: '',
  reportSubtitle: '',
  authorName: '',
  pageFormat: 'A4',
  orientation: 'portrait',
  pageMargin: 24,
  enableHeader: true,
  headerProperties: {
    content: '',
    headerSubtitle: '',
    textAlign: 'left',
    image_url: '',
    image_key: '',
    image_name: '',
    original_name: '',
    logoAlign: 'left',
    logoHeight: 28,
    borderWidth: 1,
    dividerStyle: 'solid',
    borderColor: '#e2e8f0',
    fontSize: 13,
    fontWeight: 'bold',
    color: '#0f172a',
    skipOnFirstPage: false
  },
  enableFooter: true,
  footerProperties: {
    content: '',
    textAlign: 'left',
    showPagination: true,
    paginationFormat: 'page_of_total',
    paginationAlign: 'right',
    footerDateFormat: 'short',
    dateAlign: 'center',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    dividerStyle: 'solid',
    fontSize: 12,
    fontWeight: 'normal',
    color: '#64748b'
  },
  enableWatermark: false,
  watermarkSettings: {
    enabled: false,
    type: 'text',
    text: '',
    fontSize: 54,
    color: '#94a3b8',
    opacity: 0.12,
    rotation: -45,
    skipOnFirstPage: false
  }
};

const createDefaultPages = (_settings: ReportGlobalSettings = DEFAULT_GLOBAL_SETTINGS): ReportPage[] => [
  {
    id: `page-${Date.now()}`,
    widgets: []
  }
];

const CustomReportsMain: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const clientLevel = useClientLevel();
  const activeClientId = clientLevel?.id && clientLevel.name !== 'global' ? (Number(clientLevel.id) || clientLevel.id) : (clientLevel?.id ? (Number(clientLevel.id) || clientLevel.id) : null);

  const initialClientKeyRef = useRef<string>(`${clientLevel?.id || ''}_${clientLevel?.name || ''}`);

  // Redirect outside the builder if the user switches clients while inside
  useEffect(() => {
    const currentKey = `${clientLevel?.id || ''}_${clientLevel?.name || ''}`;
    if (initialClientKeyRef.current !== currentKey) {
      if (clientLevel?.name === 'global') {
        navigate('/dashboard');
      } else {
        navigate('/customreports');
      }
    }
  }, [clientLevel?.id, clientLevel?.name, navigate]);

  const [activeReportId, setActiveReportId] = useState<string>(id || `rep-${Date.now()}`);
  const [reportMeta, setReportMeta] = useState<{
    name: string;
    description: string;
    version: string;
    status: 'draft' | 'published' | 'archived';
    author: string;
  }>({
    name: '',
    description: '',
    version: '1.0.0',
    status: 'draft',
    author: ''
  });

  const [globalSettings, setGlobalSettings] = useState<ReportGlobalSettings>(DEFAULT_GLOBAL_SETTINGS);
  const [pages, setPages] = useState<ReportPage[]>(() => createDefaultPages());
  const [activePageId, setActivePageId] = useState<string>(() => pages[0]?.id || `page-${Date.now()}`);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeSidebarTab, setActiveSidebarTab] = useState<CanvaSidebarTab | null>('elements');
  const [isRightPropsOpen, setIsRightPropsOpen] = useState(true);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [editingWidgetId, setEditingWidgetId] = useState<string | null>(null);
  const [confirmModalType, setConfirmModalType] = useState<'leave' | 'refresh' | null>(null);
  const [deletedWidgetIds, setDeletedWidgetIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const [, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);
  const isInitialLoadingRef = useRef(true);

  const markDirty = () => {
    if (!isDirtyRef.current) {
      isDirtyRef.current = true;
      setIsDirty(true);
    }
  };

  const savedSnapshotRef = useRef<string>('');

  const serializeReportState = (meta: any, gs: any, pgs: ReportPage[], delIds: string[] = []) => {
    try {
      return JSON.stringify({
        meta: {
          name: meta?.name || '',
          description: meta?.description || '',
          version: meta?.version || '',
          status: meta?.status || 'draft',
          author: meta?.author || ''
        },
        globalSettings: gs || {},
        pages: (pgs || []).map((page) => ({
          id: page.id,
          widgets: (page.widgets || []).map((widget) => ({
            id: widget.id,
            type: widget.type,
            x: widget.x,
            y: widget.y,
            w: widget.w,
            h: widget.h,
            properties: widget.properties || {}
          }))
        })),
        deletedWidgetIds: [...(delIds || [])].sort()
      });
    } catch {
      return '';
    }
  };

  const hasUnsavedChanges = () => {
    if (!isDirtyRef.current) return false;
    if (!savedSnapshotRef.current) return false;
    const current = serializeReportState(reportMeta, globalSettings, pages, deletedWidgetIds);
    return current !== savedSnapshotRef.current;
  };

  const MAX_PAGE_ROWS = 26;
  const TOTAL_COLS = 12;

  const safeJsonParse = (val: any, fallback: any = {}) => {
    if (!val) return fallback;
    if (typeof val === 'object') return val;
    if (typeof val === 'string') {
      try {
        return JSON.parse(val);
      } catch {
        return fallback;
      }
    }
    return fallback;
  };

  // Load report from API on mount or ID change
  useEffect(() => {
    const loadReport = async () => {
      if (id) {
        try {
          const queryObj: any = {
            ...(activeClientId ? { client_id: Number(activeClientId) || activeClientId } : {})
          };
          const res = await baseapi.get(`custom_reports/${id}`, {
            params: {
              query: JSON.stringify(queryObj)
            }
          });
          const raw = res.data;
          const reportMetaObj = raw?.data || raw;
          if (reportMetaObj) {
            const reportId = reportMetaObj.id || reportMetaObj._id || reportMetaObj.report_id || id;
            setActiveReportId(String(reportId));
            const loadedMeta = {
              name: reportMetaObj.report_name || reportMetaObj.name || reportMetaObj.title || '',
              description: reportMetaObj.description || '',
              version: reportMetaObj.version || '1.0.0',
              status: (reportMetaObj.status as 'draft' | 'published' | 'archived') || 'draft',
              author: reportMetaObj.author || ''
            };
            setReportMeta(loadedMeta);

            // Extract global settings from raw root or data wrapper
            const rawGlobalSettings = raw?.global_settings || raw?.globalSettings || reportMetaObj?.global_settings || reportMetaObj?.globalSettings || {};
            const extraSettings = safeJsonParse(rawGlobalSettings.extra_settings || rawGlobalSettings.extraSettings, {});
            const rawHeaderProps = safeJsonParse(rawGlobalSettings.header_properties || rawGlobalSettings.headerProperties, {});
            const rawFooterProps = safeJsonParse(rawGlobalSettings.footer_properties || rawGlobalSettings.footerProperties, {});

            const loadedSettings: ReportGlobalSettings = {
              reportTitle: rawGlobalSettings.report_name || rawGlobalSettings.reportTitle || reportMetaObj.report_name || reportMetaObj.name || '',
              reportSubtitle: '',
              authorName: rawGlobalSettings.authorName || rawGlobalSettings.author_name || extraSettings.authorName || reportMetaObj.author || '',
              pageFormat: rawGlobalSettings.page_format || rawGlobalSettings.pageFormat || 'A4',
              pageMargin: rawGlobalSettings.page_margin !== undefined ? Number(rawGlobalSettings.page_margin) : 24,
              enableHeader: rawGlobalSettings.enable_header !== undefined ? Boolean(rawGlobalSettings.enable_header) : true,
              headerProperties: {
                content: rawHeaderProps.content !== undefined ? rawHeaderProps.content : '',
                headerSubtitle: rawHeaderProps.headerSubtitle !== undefined ? rawHeaderProps.headerSubtitle : (rawHeaderProps.header_subtitle !== undefined ? rawHeaderProps.header_subtitle : ''),
                textAlign: rawHeaderProps.textAlign || rawHeaderProps.text_align || 'left',
                image_url: rawHeaderProps.image_url || rawHeaderProps.logoUrl || rawHeaderProps.logo_url || (rawHeaderProps.image_key ? `/api/${rawHeaderProps.image_key}` : ''),
                image_key: rawHeaderProps.image_key || rawHeaderProps.imageKey || '',
                image_name: rawHeaderProps.image_name || rawHeaderProps.imageName || '',
                original_name: rawHeaderProps.original_name || rawHeaderProps.originalName || '',
                size: rawHeaderProps.size,
                content_type: rawHeaderProps.content_type || rawHeaderProps.contentType,
                logoAlign: rawHeaderProps.logoAlign || rawHeaderProps.logo_align || 'left',
                logoHeight: Number(rawHeaderProps.logoHeight || rawHeaderProps.logo_height || 28),
                borderWidth: rawHeaderProps.borderWidth !== undefined ? Number(rawHeaderProps.borderWidth) : (rawHeaderProps.border_width !== undefined ? Number(rawHeaderProps.border_width) : 1),
                dividerStyle: rawHeaderProps.dividerStyle || rawHeaderProps.divider_style || 'solid',
                borderColor: rawHeaderProps.borderColor || rawHeaderProps.border_color || '#e2e8f0',
                fontSize: Number(rawHeaderProps.fontSize || 13),
                fontWeight: rawHeaderProps.fontWeight || 'bold',
                color: rawHeaderProps.color || '#0f172a',
                skipOnFirstPage: rawHeaderProps.skipOnFirstPage !== undefined ? Boolean(rawHeaderProps.skipOnFirstPage) : (rawHeaderProps.skip_on_first_page !== undefined ? Boolean(rawHeaderProps.skip_on_first_page) : false)
              },
              enableFooter: rawGlobalSettings.enable_footer !== undefined ? Boolean(rawGlobalSettings.enable_footer) : true,
              footerProperties: {
                content: rawFooterProps.content !== undefined ? rawFooterProps.content : '',
                textAlign: rawFooterProps.textAlign || rawFooterProps.text_align || 'left',
                showPagination: rawFooterProps.showPagination !== undefined ? Boolean(rawFooterProps.showPagination) : (rawFooterProps.show_pagination !== undefined ? Boolean(rawFooterProps.show_pagination) : true),
                paginationFormat: rawFooterProps.paginationFormat || rawFooterProps.pagination_format || 'page_of_total',
                paginationAlign: rawFooterProps.paginationAlign || rawFooterProps.pagination_align || 'right',
                footerDateFormat: rawFooterProps.footerDateFormat || rawFooterProps.footer_date_format || 'short',
                dateAlign: rawFooterProps.dateAlign || rawFooterProps.date_align || 'center',
                borderWidth: rawFooterProps.borderWidth !== undefined ? Number(rawFooterProps.borderWidth) : (rawFooterProps.border_width !== undefined ? Number(rawFooterProps.border_width) : 1),
                borderColor: rawFooterProps.borderColor || rawFooterProps.border_color || '#e2e8f0',
                dividerStyle: rawFooterProps.dividerStyle || rawFooterProps.divider_style || 'solid',
                fontSize: Number(rawFooterProps.fontSize || 12),
                fontWeight: rawFooterProps.fontWeight || 'normal',
                color: rawFooterProps.color || '#64748b'
              },
              enableWatermark: rawGlobalSettings.enable_watermark !== undefined
                ? Boolean(rawGlobalSettings.enable_watermark)
                : (extraSettings.enable_watermark !== undefined ? Boolean(extraSettings.enable_watermark) : (extraSettings.enableWatermark !== undefined ? Boolean(extraSettings.enableWatermark) : false)),
              watermarkSettings: {
                enabled: rawGlobalSettings.enable_watermark !== undefined
                  ? Boolean(rawGlobalSettings.enable_watermark)
                  : (extraSettings.enable_watermark !== undefined ? Boolean(extraSettings.enable_watermark) : (extraSettings.enableWatermark !== undefined ? Boolean(extraSettings.enableWatermark) : false)),
                type: 'text',
                text: rawGlobalSettings.watermark_settings?.text !== undefined
                  ? rawGlobalSettings.watermark_settings.text
                  : (extraSettings.watermark_settings?.text !== undefined ? extraSettings.watermark_settings.text : (extraSettings.watermarkSettings?.text !== undefined ? extraSettings.watermarkSettings.text : '')),
                fontSize: Number(rawGlobalSettings.watermark_settings?.fontSize || extraSettings.watermark_settings?.fontSize || extraSettings.watermarkSettings?.fontSize || 54),
                color: rawGlobalSettings.watermark_settings?.color || extraSettings.watermark_settings?.color || extraSettings.watermarkSettings?.color || '#94a3b8',
                opacity: rawGlobalSettings.watermark_settings?.opacity !== undefined
                  ? Number(rawGlobalSettings.watermark_settings.opacity)
                  : (extraSettings.watermark_settings?.opacity !== undefined ? Number(extraSettings.watermark_settings.opacity) : (extraSettings.watermarkSettings?.opacity !== undefined ? Number(extraSettings.watermarkSettings.opacity) : 0.12)),
                rotation: rawGlobalSettings.watermark_settings?.rotation !== undefined
                  ? Number(rawGlobalSettings.watermark_settings.rotation)
                  : (extraSettings.watermark_settings?.rotation !== undefined ? Number(extraSettings.watermark_settings.rotation) : (extraSettings.watermarkSettings?.rotation !== undefined ? Number(extraSettings.watermarkSettings.rotation) : -45)),
                skipOnFirstPage: rawGlobalSettings.watermark_settings?.skipOnFirstPage !== undefined
                  ? Boolean(rawGlobalSettings.watermark_settings.skipOnFirstPage)
                  : (extraSettings.watermark_settings?.skipOnFirstPage !== undefined ? Boolean(extraSettings.watermark_settings.skipOnFirstPage) : (extraSettings.watermarkSettings?.skipOnFirstPage !== undefined ? Boolean(extraSettings.watermarkSettings.skipOnFirstPage) : false))
              }
            };
            setGlobalSettings(loadedSettings);

            // Extract raw widgets from root or data wrapper
            const rawWidgets = Array.isArray(raw?.widgets)
              ? raw.widgets
              : (Array.isArray(reportMetaObj?.widgets) ? reportMetaObj.widgets : []);

            let safePages: ReportPage[] = [];
            if (rawWidgets.length > 0) {
              const pageMap = new Map<number, WidgetItem[]>();
              rawWidgets.forEach((w: any, wIdx: number) => {
                if (w.widget_type === 'header' || w.widget_type === 'footer' || w.type === 'header' || w.type === 'footer') {
                  return;
                }
                const pageNum = Number(w.page_number) || 1;
                const pList = pageMap.get(pageNum) || [];
                const parsedProperties = safeJsonParse(w.properties, {});
                const mergedProperties = {
                  ...parsedProperties,
                  image_url: w.image_url || parsedProperties.image_url || '',
                  image_key: w.image_key || parsedProperties.image_key || '',
                  image_name: w.image_name || parsedProperties.image_name || '',
                  original_name: w.original_name || parsedProperties.original_name || ''
                };
                pList.push({
                  id: String(w.id !== undefined ? w.id : `w-${wIdx}`),
                  type: (w.widget_type || w.type || 'text') as WidgetType,
                  x: Number.isFinite(Number(w.pos_x)) ? Number(w.pos_x) : (Number.isFinite(Number(w.x)) ? Number(w.x) : 0),
                  y: Number.isFinite(Number(w.pos_y)) ? Number(w.pos_y) : (Number.isFinite(Number(w.y)) ? Number(w.y) : 0),
                  w: Number.isFinite(Number(w.width)) ? Number(w.width) : (Number.isFinite(Number(w.w)) ? Number(w.w) : 6),
                  h: Number.isFinite(Number(w.height)) ? Number(w.height) : (Number.isFinite(Number(w.h)) ? Number(w.h) : 4),
                  properties: mergedProperties
                });
                pageMap.set(pageNum, pList);
              });

              // Ensure we build all pages from 1 to the highest page_number
              const pageConfigs = (extraSettings as any)?.page_configs || {};
              const maxPageNum = Math.max(1, ...Array.from(pageMap.keys()));
              safePages = Array.from({ length: maxPageNum }, (_, idx) => {
                const pNum = idx + 1;
                const cfg = pageConfigs[`page_${pNum}`] || pageConfigs[`page-${pNum}`] || {};
                return {
                  id: `page-${pNum}`,
                  name: cfg.name || '',
                  pinned: cfg.pinned || 'none',
                  widgets: pageMap.get(pNum) || []
                };
              });
            } else if (Array.isArray(reportMetaObj.pages) && reportMetaObj.pages.length > 0) {
              const pageConfigs = (extraSettings as any)?.page_configs || {};
              safePages = reportMetaObj.pages.filter(Boolean).map((p: any, pIdx: number) => {
                const pNum = pIdx + 1;
                const cfg = pageConfigs[`page_${pNum}`] || pageConfigs[`page-${pNum}`] || pageConfigs[p?.id] || {};
                return {
                  id: String(p?.id || p?._id || `page-${pNum}`),
                  name: p?.name || cfg.name || '',
                  pinned: p?.pinned || cfg.pinned || 'none',
                  widgets: Array.isArray(p?.widgets) ? p.widgets.filter(Boolean).map((w: any, wIdx: number) => {
                    const parsedProps = safeJsonParse(w?.properties, {});
                    const mergedProps = {
                      ...parsedProps,
                      image_url: w?.image_url || parsedProps.image_url || '',
                      image_key: w?.image_key || parsedProps.image_key || '',
                      image_name: w?.image_name || parsedProps.image_name || '',
                      original_name: w?.original_name || parsedProps.original_name || ''
                    };
                    return {
                      id: String(w?.id || w?._id || `w-${wIdx}`),
                      type: (w?.widget_type || w?.type || 'text') as WidgetType,
                      x: Number.isFinite(Number(w?.pos_x)) ? Number(w.pos_x) : (Number.isFinite(Number(w?.x)) ? Number(w.x) : 0),
                      y: Number.isFinite(Number(w?.pos_y)) ? Number(w.pos_y) : (Number.isFinite(Number(w?.y)) ? Number(w.y) : 0),
                      w: Number.isFinite(Number(w?.width)) ? Number(w.width) : (Number.isFinite(Number(w?.w)) ? Number(w.w) : 6),
                      h: Number.isFinite(Number(w?.height)) ? Number(w.height) : (Number.isFinite(Number(w?.h)) ? Number(w.h) : 4),
                      properties: mergedProps
                    };
                  }) : []
                };
              });
            } else {
              safePages = createDefaultPages(loadedSettings);
            }

            setPages(safePages);
            setActivePageId(safePages[0]?.id || `page-${Date.now()}`);
            setDeletedWidgetIds([]);
            savedSnapshotRef.current = serializeReportState(loadedMeta, loadedSettings, safePages, []);
            isDirtyRef.current = false;
            setIsDirty(false);
            isInitialLoadingRef.current = true;
            setTimeout(() => {
              isInitialLoadingRef.current = false;
            }, 800);
            return;
          }
        } catch (err: any) {
          showError(err.response?.data?.message || err.message || 'Failed to fetch report from server');
        }
      }

      // If no valid ID, create a fresh report document and save to API
      const newId = `rep-${Date.now()}`;
      const freshPages = createDefaultPages(DEFAULT_GLOBAL_SETTINGS);

      try {
        const createRes = await baseapi.post('custom_reports', {
          report_name: 'Untitled Custom Report',
          description: '',
          client_id: activeClientId
        });
        const createdData = createRes.data?.data || createRes.data;
        const createdId = createdData?.id || newId;
        setActiveReportId(createdId);
        navigate(`/customreports/builder/${createdId}`, { replace: true });
      } catch {
        setActiveReportId(newId);
        navigate(`/customreports/builder/${newId}`, { replace: true });
      }

      const freshMeta = {
        name: 'Untitled Custom Report',
        description: '',
        version: '1.0.0',
        status: 'draft' as const,
        author: ''
      };
      setReportMeta(freshMeta);
      setGlobalSettings(DEFAULT_GLOBAL_SETTINGS);
      setPages(freshPages);
      setActivePageId(freshPages[0]?.id || `page-${Date.now()}`);
      setDeletedWidgetIds([]);
      savedSnapshotRef.current = serializeReportState(freshMeta, DEFAULT_GLOBAL_SETTINGS, freshPages, []);
      isDirtyRef.current = false;
      setIsDirty(false);
      isInitialLoadingRef.current = true;
      setTimeout(() => {
        isInitialLoadingRef.current = false;
      }, 800);
    };

    loadReport();
  }, [id]);

  // Persist full report document & all widgets
  const handleSaveCurrentReport = async () => {
    setIsSaving(true);
    try {
      // 1. Update report metadata via PUT /custom_reports/{id}
      await baseapi.put(`custom_reports/${activeReportId}`, {
        report_name: globalSettings.reportTitle || reportMeta.name || 'Untitled Report',
        description: reportMeta.description || null
      });

      // 2. Update global settings via PUT /custom_reports/{id}/global-settings
      const cleanHeaderProps = { ...(globalSettings.headerProperties || {}) };
      delete cleanHeaderProps.image_url;

      const pageConfigs: Record<string, any> = {};
      (pages || []).forEach((p, pIdx) => {
        pageConfigs[`page_${pIdx + 1}`] = {
          pinned: p.pinned || 'none',
          name: p.name || ''
        };
      });

      await baseapi.put(`custom_reports/${activeReportId}/global-settings`, {
        page_format: globalSettings.pageFormat || 'A4',
        page_margin: globalSettings.pageMargin || 24,
        enable_header: globalSettings.enableHeader,
        header_properties: cleanHeaderProps,
        enable_footer: globalSettings.enableFooter,
        footer_properties: globalSettings.footerProperties,
        enable_watermark: Boolean(globalSettings.enableWatermark || globalSettings.watermarkSettings?.enabled),
        watermark_settings: globalSettings.watermarkSettings,
        extra_settings: {
          reportSubtitle: globalSettings.reportSubtitle,
          authorName: globalSettings.authorName,
          enable_watermark: Boolean(globalSettings.enableWatermark || globalSettings.watermarkSettings?.enabled),
          watermark_settings: globalSettings.watermarkSettings,
          page_configs: pageConfigs
        }
      });

      // 3. Robust Widget Reconciliation: Fetch all current server widgets to identify & remove orphans
      if (isIntegerId(activeReportId)) {
        let serverWidgets: any[] = [];
        try {
          const checkRes = await baseapi.get(`custom_reports/${activeReportId}`);
          const rawCheck = checkRes.data?.data || checkRes.data;
          serverWidgets = Array.isArray(rawCheck?.widgets) ? rawCheck.widgets : [];
        } catch (checkErr) {
          console.warn('Could not fetch server widgets for reconciliation:', checkErr);
        }

        // Identify all integer IDs currently present in active pages
        const activeIntegerIds = new Set<number>();
        (pages || []).forEach(p => {
          (p.widgets || []).forEach(w => {
            if (w && isIntegerId(w.id)) {
              activeIntegerIds.add(Number(w.id));
            }
          });
        });

        // Any server widget not in activeIntegerIds is an orphan and must be deleted
        const orphanServerIds = serverWidgets
          .map((w: any) => Number(w.id))
          .filter((sId: number) => isIntegerId(sId) && !activeIntegerIds.has(sId));

        const allIdsToDelete = Array.from(
          new Set([...deletedWidgetIds.map(Number), ...orphanServerIds])
        );

        if (allIdsToDelete.length > 0) {
          await Promise.all(
            allIdsToDelete.map(wId =>
              baseapi.delete(`custom_reports/${activeReportId}/widgets/${wId}`).catch((delErr) => {
                console.warn(`Failed to delete orphan widget ${wId}:`, delErr);
              })
            )
          );
          setDeletedWidgetIds([]);
        }

        // 4. Batch persist/update all current widgets across all pages
        const existingWidgetUpdates: any[] = [];
        const newWidgetCreates: Promise<any>[] = [];

        (pages || []).forEach((page, pIdx) => {
          const pageNum = pIdx + 1;
          (page.widgets || []).forEach((widget) => {
            if (!widget) return;
            // Header and Footer are global settings (header_properties / footer_properties),
            // they must NOT be persisted in the widgets table / bulk update payload.
            if (widget.type === 'header' || widget.type === 'footer') return;

            const cleanProperties = { ...(widget.properties || {}) };
            delete cleanProperties.previewResult;
            delete cleanProperties.image_url;

            const payload = {
              page_number: pageNum,
              widget_type: widget.type,
              pos_x: widget.x,
              pos_y: widget.y,
              width: widget.w,
              height: widget.h,
              properties: cleanProperties
            };

            if (isIntegerId(widget.id)) {
              existingWidgetUpdates.push({
                id: Number(widget.id),
                ...payload
              });
            } else {
              newWidgetCreates.push(
                baseapi.post(`custom_reports/${activeReportId}/widgets`, payload).then(res => {
                  const bId = res.data?.data?.id || res.data?.id;
                  if (bId) {
                    widget.id = String(bId);
                  }
                }).catch(postErr => {
                  console.error('Failed to create new widget:', postErr);
                })
              );
            }
          });
        });

        // Use bulk update request or fallback to individual PUTs
        if (existingWidgetUpdates.length > 0) {
          try {
            await baseapi.put(`custom_reports/${activeReportId}/widgets/bulk`, existingWidgetUpdates);
          } catch {
            await Promise.all(
              existingWidgetUpdates.map(w =>
                baseapi.put(`custom_reports/${activeReportId}/widgets/${w.id}`, w).catch(putErr => {
                  console.error(`Failed to update widget ${w.id}:`, putErr);
                })
              )
            );
          }
        }

        if (newWidgetCreates.length > 0) {
          await Promise.all(newWidgetCreates);
        }
      }

      showSuccess('Report saved successfully!');
      const updatedPages = (pages || []).map(p => ({
        ...p,
        widgets: (p.widgets || []).map(w => ({ ...w }))
      }));
      setPages(updatedPages);
      setDeletedWidgetIds([]);
      savedSnapshotRef.current = serializeReportState(reportMeta, globalSettings, updatedPages, []);
      isDirtyRef.current = false;
      setIsDirty(false);
      isInitialLoadingRef.current = true;
      setTimeout(() => {
        isInitialLoadingRef.current = false;
      }, 800);
      return true;
    } catch (err: any) {
      showError(err.response?.data?.message || err.message || 'Failed to save report');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackToReports = () => {
    if (!hasUnsavedChanges()) {
      navigate('/customreports');
      return;
    }
    setConfirmModalType('leave');
  };

  const handleConfirmDiscardAndLeave = () => {
    setConfirmModalType(null);
    isDirtyRef.current = false;
    setIsDirty(false);
    navigate('/customreports');
  };

  const handleSaveAndLeave = async () => {
    const success = await handleSaveCurrentReport();
    if (success) {
      setConfirmModalType(null);
      navigate('/customreports');
    }
  };

  const handleRefresh = () => {
    if (!hasUnsavedChanges()) {
      window.location.reload();
      return;
    }
    setConfirmModalType('refresh');
  };

  const handleConfirmDiscardAndRefresh = () => {
    setConfirmModalType(null);
    isDirtyRef.current = false;
    setIsDirty(false);
    window.location.reload();
  };

  const handleCancelConfirmModal = () => {
    setConfirmModalType(null);
  };

  // Handle native browser reload / close tab warning when changes exist
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [pages, globalSettings, reportMeta, deletedWidgetIds]);

  // Intercept F5 and Ctrl+R / Cmd+R to show in-app modal when changes exist
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F5' || ((e.ctrlKey || e.metaKey) && (e.key === 'r' || e.key === 'R'))) {
        if (hasUnsavedChanges()) {
          e.preventDefault();
          setConfirmModalType('refresh');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [pages, globalSettings, reportMeta, deletedWidgetIds]);

  const isIntegerId = (val: string | number | undefined | null): boolean => {
    if (val === undefined || val === null) return false;
    const str = String(val).trim();
    return /^\d+$/.test(str);
  };

  const findBestWidgetPosition = (
    pagesList: ReportPage[],
    currentPageId: string,
    w: number,
    h: number
  ) => {
    const currentPage = (pagesList || []).find(p => p && p.id === currentPageId);
    const widgets = currentPage ? (currentPage.widgets || []).filter(Boolean) : [];

    const overlaps = (widgetList: WidgetItem[], x: number, y: number, width: number, height: number) => {
      return widgetList.some(other => {
        if (!other) return false;
        const ox = Number.isFinite(other.x) ? other.x : 0;
        const oy = Number.isFinite(other.y) ? other.y : 0;
        const ow = other.w || 4;
        const oh = other.h || 4;
        return !(x + width <= ox || ox + ow <= x || y + height <= oy || oy + oh <= y);
      });
    };

    for (let y = 0; y + h <= MAX_PAGE_ROWS; y++) {
      for (let x = 0; x + w <= TOTAL_COLS; x++) {
        if (!overlaps(widgets, x, y, w, h)) {
          return { targetPageId: currentPageId, x, y, createNewPage: false };
        }
      }
    }

    const currentIndex = (pagesList || []).findIndex(p => p && p.id === currentPageId);
    if (currentIndex !== -1 && currentIndex < pagesList.length - 1) {
      const nextPage = pagesList[currentIndex + 1];
      if (nextPage) {
        for (let y = 0; y + h <= MAX_PAGE_ROWS; y++) {
          for (let x = 0; x + w <= TOTAL_COLS; x++) {
            if (!overlaps(nextPage.widgets || [], x, y, w, h)) {
              return { targetPageId: nextPage.id, x, y, createNewPage: false };
            }
          }
        }
      }
    }

    const newPageId = `page-${Date.now()}`;
    return { targetPageId: newPageId, x: 0, y: 0, createNewPage: true };
  };

  // Add Widget Tool Action (In-Memory)
  const handleAddWidget = (type: WidgetType) => {
    if (type === 'chart') {
      setEditingWidgetId(null);
      setIsChartModalOpen(true);
      return;
    }

    if (type === 'header') {
      const targetPage = (pages || []).find(p => p && p.id === activePageId);
      const existingHeader = targetPage?.widgets?.find(w => w && w.type === 'header');
      if (existingHeader) {
        setSelectedId(existingHeader.id);
        return;
      }

      const headerId = `header-${Date.now()}`;
      const headerWidget: WidgetItem = {
        id: headerId,
        type: 'header',
        x: 0,
        y: 0,
        w: 12,
        h: 1,
        properties: {
          content: globalSettings.headerProperties?.content !== undefined ? globalSettings.headerProperties.content : '',
          headerSubtitle: globalSettings.headerProperties?.headerSubtitle !== undefined ? globalSettings.headerProperties.headerSubtitle : (globalSettings.reportSubtitle || ''),
          textAlign: globalSettings.headerProperties?.textAlign || 'left',
          image_url: globalSettings.headerProperties?.image_url || '',
          logoAlign: globalSettings.headerProperties?.logoAlign || 'left',
          logoHeight: globalSettings.headerProperties?.logoHeight || 28,
          borderWidth: globalSettings.headerProperties?.borderWidth !== undefined ? globalSettings.headerProperties.borderWidth : 1,
          dividerStyle: globalSettings.headerProperties?.dividerStyle || 'solid',
          borderColor: globalSettings.headerProperties?.borderColor || '#e2e8f0',
          fontSize: globalSettings.headerProperties?.fontSize || 13,
          fontWeight: globalSettings.headerProperties?.fontWeight || 'bold',
          color: globalSettings.headerProperties?.color || '#0f172a',
          skipOnFirstPage: globalSettings.headerProperties?.skipOnFirstPage || false
        }
      };

      markDirty();
      setPages(prev => (prev || []).map(page =>
        page && page.id === activePageId
          ? { ...page, widgets: [headerWidget, ...(page.widgets || [])] }
          : page
      ));
      setSelectedId(headerId);
      return;
    }

    if (type === 'footer') {
      const targetPage = (pages || []).find(p => p && p.id === activePageId);
      const existingFooter = targetPage?.widgets?.find(w => w && w.type === 'footer');
      if (existingFooter) {
        setSelectedId(existingFooter.id);
        return;
      }

      const footerId = `footer-${Date.now()}`;
      const footerWidget: WidgetItem = {
        id: footerId,
        type: 'footer',
        x: 0,
        y: 24,
        w: 12,
        h: 1,
        properties: {
          content: globalSettings.footerProperties?.content !== undefined ? globalSettings.footerProperties.content : '',
          textAlign: globalSettings.footerProperties?.textAlign || 'left',
          paginationAlign: globalSettings.footerProperties?.paginationAlign || 'right',
          paginationFormat: globalSettings.footerProperties?.paginationFormat || 'page_of_total',
          borderWidth: globalSettings.footerProperties?.borderWidth !== undefined ? globalSettings.footerProperties.borderWidth : 1,
          dividerStyle: globalSettings.footerProperties?.dividerStyle || 'solid',
          borderColor: globalSettings.footerProperties?.borderColor || '#e2e8f0',
          fontSize: globalSettings.footerProperties?.fontSize || 12,
          fontWeight: globalSettings.footerProperties?.fontWeight || 'normal',
          color: globalSettings.footerProperties?.color || '#64748b'
        }
      };

      markDirty();
      setPages(prev => (prev || []).map(page =>
        page && page.id === activePageId
          ? { ...page, widgets: [...(page.widgets || []), footerWidget] }
          : page
      ));
      setSelectedId(footerId);
      return;
    }

    const w = type === 'divider' || type === 'heading' ? 12 : 6;
    const h = type === 'divider' ? 1 : (type === 'heading' ? 2 : (type === 'image' ? 6 : 3));
    const { targetPageId, x, y, createNewPage } = findBestWidgetPosition(pages, activePageId, w, h);

    const id = `widget-${Date.now()}`;
    const initialProps: Record<string, any> = {};
    if (type === 'heading') {
      initialProps.content = 'Heading Title';
      initialProps.fontSize = 24;
      initialProps.fontWeight = 700;
      initialProps.color = '#0f172a';
      initialProps.headingLevel = 'h1';
      initialProps.backgroundColor = 'transparent';
      initialProps.borderWidth = 0;
      initialProps.borderLeftWidth = 0;
      initialProps.borderBottomWidth = 0;
    } else if (type === 'text') {
      initialProps.content = 'Type descriptive narrative and detailed paragraphs here.';
      initialProps.fontSize = 14;
      initialProps.fontWeight = 400;
      initialProps.color = '#334155';
      initialProps.backgroundColor = 'transparent';
      initialProps.borderWidth = 0;
      initialProps.borderLeftWidth = 0;
      initialProps.borderBottomWidth = 0;
    } else if (type === 'divider') {
      initialProps.color = '#e2e8f0';
      initialProps.borderWidth = 1;
      initialProps.dividerStyle = 'solid';
    }

    const newWidget: WidgetItem = {
      id,
      type,
      x,
      y,
      w,
      h,
      properties: initialProps
    };

    markDirty();
    if (createNewPage) {
      setPages(prev => [...(prev || []), { id: targetPageId, widgets: [newWidget] }]);
      setActivePageId(targetPageId);
    } else {
      setPages(prev => (prev || []).map(page =>
        page && page.id === targetPageId
          ? { ...page, widgets: [...(page.widgets || []), newWidget] }
          : page
      ));
      setActivePageId(targetPageId);
    }
    setSelectedId(id);
  };

  const handleOpenChartModal = (widget?: WidgetItem) => {
    const targetWidget = widget || (selectedWidget?.type === 'chart' ? selectedWidget : null);
    if (targetWidget && targetWidget.type === 'chart') {
      setSelectedId(targetWidget.id);
      setEditingWidgetId(targetWidget.id);
    } else {
      setEditingWidgetId(null);
    }
    setIsChartModalOpen(true);
  };

  const handleEditWidget = (widget: WidgetItem) => {
    if (!widget) return;
    setSelectedId(widget.id);
    if (widget.type === 'chart') {
      handleOpenChartModal(widget);
    }
  };

  const handleAddWidgetFromModal = (payload: AddWidgetPayload) => {
    setIsChartModalOpen(false);

    const chartType = payload.chart_type || payload.widget_type || payload.chartType || 'bar';
    const isTable = chartType === 'table';
    const isMetricOrGauge = chartType === 'metric' || chartType === 'gauge';
    const w = isTable ? 12 : (isMetricOrGauge ? 4 : 6);
    const h = chartType === 'metric' ? 4 : (chartType === 'gauge' ? 6 : (isTable ? 8 : 7));

    const editingWidget = pages.flatMap(p => p.widgets || []).find(w => w?.id === editingWidgetId);

    const widgetProperties: Partial<WidgetProperties> = {
      ...(editingWidget?.properties || {}),
      widget_type: chartType,
      chart_type: chartType,
      chartType: chartType,
      chartTitle: payload.title || payload.name || payload.chartTitle || '',
      title: payload.title || payload.name || payload.chartTitle || '',
      name: payload.title || payload.name || payload.chartTitle || '',
      colorPalette: payload.colorPalette || editingWidget?.properties?.colorPalette || 'classic',
      chartColors: payload.chartColors || editingWidget?.properties?.chartColors || ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'],
      accentColor: payload.accentColor || editingWidget?.properties?.accentColor || '#3b82f6',
      query_definition: payload.query_definition,
      visualization_config: payload.visualization_config,
      dataSource: payload.query_definition?.source || payload.dataSource || payload.dataset_name || '',
      dataset_name: payload.query_definition?.source || payload.dataSource || payload.dataset_name || '',
      metric_field: payload.query_definition?.metrics?.[0]?.field || payload.metric_field || payload.yAxisField || '',
      yAxisField: payload.query_definition?.metrics?.[0]?.field || payload.metric_field || payload.yAxisField || '',
      aggregation: payload.query_definition?.metrics?.[0]?.type || payload.aggregation || 'count',
      yAxisAggregation: payload.query_definition?.metrics?.[0]?.type || payload.aggregation || 'count',
      group_by_field: payload.query_definition?.x_axis?.field || payload.xAxisField || payload.group_by_field || '',
      xAxisField: payload.query_definition?.x_axis?.field || payload.xAxisField || payload.group_by_field || '',
      dimensions: payload.dimensions || (payload.query_definition?.x_axis?.field ? [payload.query_definition.x_axis.field] : []),
      group_by_fields: payload.group_by_fields || (payload.query_definition?.x_axis?.field ? [payload.query_definition.x_axis.field] : []),
      columns: payload.query_definition?.columns || payload.columns || [],
      filters: payload.query_definition?.filters || payload.filters || [],
      time_field: payload.time_field || null,
      time_granularity: payload.time_granularity || null,
      custom_options: payload.custom_options || {},
      ...(payload.custom_options || {})
    };

    if (editingWidgetId) {
      handleUpdateWidget(editingWidgetId, widgetProperties);
      setEditingWidgetId(null);
    } else {
      const { targetPageId, x, y, createNewPage } = findBestWidgetPosition(pages, activePageId, w, h);

      const id = `widget-${Date.now()}`;
      const newWidget: WidgetItem = {
        id,
        type: (['heading', 'text', 'divider', 'image'].includes(chartType) ? chartType : 'chart') as WidgetType,
        x,
        y,
        w,
        h,
        properties: widgetProperties
      };

      markDirty();
      if (createNewPage) {
        setPages(prev => [...(prev || []), { id: targetPageId, widgets: [newWidget] }]);
        setActivePageId(targetPageId);
      } else {
        setPages(prev => (prev || []).map(page =>
          page && page.id === targetPageId
            ? { ...page, widgets: [...(page.widgets || []), newWidget] }
            : page
        ));
        setActivePageId(targetPageId);
      }
      setSelectedId(id);
    }
  };

  const handleUpdateWidget = (id: string, updates: Partial<WidgetItem['properties']> & { w?: number; h?: number; x?: number; y?: number }) => {
    markDirty();
    const { w: width, h: height, x: posX, y: posY, ...propUpdates } = updates as any;
    setPages(prev => (prev || []).map(page => ({
      ...page,
      widgets: (page.widgets || []).map(w => {
        if (!w || w.id !== id) return w;
        return {
          ...w,
          ...(width !== undefined ? { w: width } : {}),
          ...(height !== undefined ? { h: height } : {}),
          ...(posX !== undefined ? { x: posX } : {}),
          ...(posY !== undefined ? { y: posY } : {}),
          properties: { ...w.properties, ...propUpdates }
        };
      })
    })));
  };

  const handleDeleteWidget = (id: string) => {
    markDirty();
    setPages(prev => (prev || []).map(page => ({
      ...page,
      widgets: (page.widgets || []).filter(w => w && w.id !== id)
    })));
    if (selectedId === id) {
      setSelectedId(null);
    }
    if (editingWidgetId === id) {
      setEditingWidgetId(null);
    }

    if (isIntegerId(id)) {
      setDeletedWidgetIds(prev => [...prev, id]);
    }
  };

  const handleDuplicateWidget = (id: string) => {
    const activePage = (pages || []).find(p => p && p.id === activePageId);
    if (!activePage) return;
    const targetWidget = (activePage.widgets || []).find(w => w && w.id === id);
    if (!targetWidget) return;

    const w = targetWidget.w || 6;
    const h = targetWidget.h || 6;
    const { targetPageId, x, y, createNewPage } = findBestWidgetPosition(pages, activePageId, w, h);

    const newId = `widget-${Date.now()}`;
    const duplicatedWidget: WidgetItem = {
      ...targetWidget,
      id: newId,
      x,
      y,
      w,
      h
    };

    markDirty();
    if (createNewPage) {
      setPages(prev => [...(prev || []), { id: targetPageId, widgets: [duplicatedWidget] }]);
      setActivePageId(targetPageId);
    } else {
      setPages(prev => (prev || []).map(page =>
        page && page.id === targetPageId
          ? { ...page, widgets: [...(page.widgets || []), duplicatedWidget] }
          : page
      ));
      setActivePageId(targetPageId);
    }
    setSelectedId(newId);
  };

  const handleMoveWidgetToPage = (widgetId: string, direction: 'prev' | 'next') => {
    const sourcePageIndex = (pages || []).findIndex(p => p && p.widgets?.some(w => w && w.id === widgetId));
    if (sourcePageIndex === -1) return;

    const sourcePage = pages[sourcePageIndex];
    const targetWidget = (sourcePage.widgets || []).find(w => w && w.id === widgetId);
    if (!targetWidget) return;

    const targetPageIndex = direction === 'next' ? sourcePageIndex + 1 : sourcePageIndex - 1;
    if (targetPageIndex < 0) return;

    let targetPageId: string;
    let createNewPage = false;
    if (targetPageIndex >= pages.length) {
      targetPageId = `page-${Date.now()}`;
      createNewPage = true;
    } else {
      targetPageId = pages[targetPageIndex].id;
    }

    const w = targetWidget.w || 6;
    const h = targetWidget.h || 6;
    const { x, y } = findBestWidgetPosition(pages, targetPageId, w, h);

    const movedWidget: WidgetItem = {
      ...targetWidget,
      x,
      y
    };

    markDirty();
    if (createNewPage) {
      setPages(prev => {
        const updated = (prev || []).map(p =>
          p.id === sourcePage.id
            ? { ...p, widgets: (p.widgets || []).filter(w => w.id !== widgetId) }
            : p
        );
        return [...updated, { id: targetPageId, widgets: [movedWidget] }];
      });
      showSuccess(`Moved element to new Page ${targetPageIndex + 1}`);
    } else {
      setPages(prev =>
        (prev || []).map(p => {
          if (p.id === sourcePage.id) {
            return { ...p, widgets: (p.widgets || []).filter(w => w.id !== widgetId) };
          }
          if (p.id === targetPageId) {
            return { ...p, widgets: [...(p.widgets || []), movedWidget] };
          }
          return p;
        })
      );
      showSuccess(`Moved element to Page ${targetPageIndex + 1}`);
    }

    setActivePageId(targetPageId);
    setSelectedId(widgetId);

    setTimeout(() => {
      document.getElementById(`report-page-${targetPageId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleLayoutChange = (pageId: string, newLayout: Layout) => {
    if (!isInitialLoadingRef.current) {
      const targetPage = (pages || []).find(p => p && p.id === pageId);
      const hasChanged = (targetPage?.widgets || []).some(w => {
        if (!w) return false;
        const item = newLayout.find(l => l.i === w.id);
        if (!item) return false;
        const clampedY = Math.max(0, Math.min(MAX_PAGE_ROWS - item.h, item.y));
        return w.x !== item.x || w.y !== clampedY || w.w !== item.w || w.h !== item.h;
      });
      if (hasChanged) {
        markDirty();
      }
    }

    setPages(prev => (prev || []).map(page => {
      if (!page || page.id !== pageId) return page;

      return {
        ...page,
        widgets: (page.widgets || []).map(w => {
          if (!w) return w;
          const layoutItem = newLayout.find(l => l.i === w.id);
          if (layoutItem) {
            const clampedY = Math.max(0, Math.min(MAX_PAGE_ROWS - layoutItem.h, layoutItem.y));
            return {
              ...w,
              x: layoutItem.x,
              y: clampedY,
              w: layoutItem.w,
              h: layoutItem.h
            };
          }
          return w;
        })
      };
    }));
  };

  const handleDropWidget = (type: WidgetType, pageId: string, gridPos?: { x: number; y: number }) => {
    setActivePageId(pageId);

    if (type === 'chart') {
      setEditingWidgetId(null);
      setIsChartModalOpen(true);
      return;
    }

    if (type === 'header') {
      const targetPage = (pages || []).find(p => p && p.id === pageId);
      const existingHeader = targetPage?.widgets?.find(w => w && w.type === 'header');
      if (existingHeader) {
        setSelectedId(existingHeader.id);
        return;
      }

      const headerId = `header-${Date.now()}`;
      const headerWidget: WidgetItem = {
        id: headerId,
        type: 'header',
        x: 0,
        y: 0,
        w: 12,
        h: 1,
        properties: {
          content: globalSettings.headerProperties?.content !== undefined ? globalSettings.headerProperties.content : '',
          headerSubtitle: globalSettings.headerProperties?.headerSubtitle || '',
          textAlign: globalSettings.headerProperties?.textAlign || 'left',
          image_url: globalSettings.headerProperties?.image_url || '',
          logoAlign: globalSettings.headerProperties?.logoAlign || 'left',
          logoHeight: globalSettings.headerProperties?.logoHeight || 28,
          borderWidth: globalSettings.headerProperties?.borderWidth !== undefined ? globalSettings.headerProperties.borderWidth : 1,
          dividerStyle: globalSettings.headerProperties?.dividerStyle || 'solid',
          borderColor: globalSettings.headerProperties?.borderColor || '#e2e8f0',
          fontSize: globalSettings.headerProperties?.fontSize || 13,
          fontWeight: globalSettings.headerProperties?.fontWeight || 'bold',
          color: globalSettings.headerProperties?.color || '#0f172a',
          skipOnFirstPage: globalSettings.headerProperties?.skipOnFirstPage || false
        }
      };

      markDirty();
      setPages(prev => (prev || []).map(p =>
        p && p.id === pageId
          ? { ...p, widgets: [headerWidget, ...(p.widgets || [])] }
          : p
      ));
      setSelectedId(headerId);
      return;
    }

    if (type === 'footer') {
      const targetPage = (pages || []).find(p => p && p.id === pageId);
      const existingFooter = targetPage?.widgets?.find(w => w && w.type === 'footer');
      if (existingFooter) {
        setSelectedId(existingFooter.id);
        return;
      }

      const footerId = `footer-${Date.now()}`;
      const footerWidget: WidgetItem = {
        id: footerId,
        type: 'footer',
        x: 0,
        y: 24,
        w: 12,
        h: 1,
        properties: {
          content: globalSettings.footerProperties?.content !== undefined ? globalSettings.footerProperties.content : '',
          textAlign: globalSettings.footerProperties?.textAlign || 'left',
          paginationAlign: globalSettings.footerProperties?.paginationAlign || 'right',
          paginationFormat: globalSettings.footerProperties?.paginationFormat || 'page_of_total',
          borderWidth: globalSettings.footerProperties?.borderWidth !== undefined ? globalSettings.footerProperties.borderWidth : 1,
          dividerStyle: globalSettings.footerProperties?.dividerStyle || 'solid',
          borderColor: globalSettings.footerProperties?.borderColor || '#e2e8f0',
          fontSize: globalSettings.footerProperties?.fontSize || 12,
          fontWeight: globalSettings.footerProperties?.fontWeight || 'normal',
          color: globalSettings.footerProperties?.color || '#64748b'
        }
      };

      markDirty();
      setPages(prev => (prev || []).map(p =>
        p && p.id === pageId
          ? { ...p, widgets: [...(p.widgets || []), footerWidget] }
          : p
      ));
      setSelectedId(footerId);
      return;
    }

    const w = type === 'divider' || type === 'heading' ? 12 : 6;
    const h = type === 'divider' ? 1 : (type === 'heading' ? 2 : (type === 'image' ? 6 : 3));

    let finalPageId = pageId;
    let finalX = gridPos ? Math.max(0, Math.min(12 - w, gridPos.x)) : 0;
    let finalY = gridPos ? Math.max(0, gridPos.y) : 0;
    let needsNewPage = false;

    if (finalY + h > MAX_PAGE_ROWS) {
      const best = findBestWidgetPosition(pages, pageId, w, h);
      finalPageId = best.targetPageId;
      finalX = best.x;
      finalY = best.y;
      needsNewPage = best.createNewPage;
    }

    const id = `widget-${Date.now()}`;
    const initialProps: Record<string, any> = {};
    if (type === 'heading') {
      initialProps.content = 'Heading Title';
      initialProps.fontSize = 24;
      initialProps.fontWeight = 700;
      initialProps.color = '#0f172a';
      initialProps.headingLevel = 'h1';
      initialProps.backgroundColor = 'transparent';
      initialProps.borderWidth = 0;
      initialProps.borderLeftWidth = 0;
      initialProps.borderBottomWidth = 0;
    } else if (type === 'text') {
      initialProps.content = 'Type descriptive narrative and detailed paragraphs here.';
      initialProps.fontSize = 14;
      initialProps.fontWeight = 400;
      initialProps.color = '#334155';
      initialProps.backgroundColor = 'transparent';
      initialProps.borderWidth = 0;
      initialProps.borderLeftWidth = 0;
      initialProps.borderBottomWidth = 0;
    } else if (type === 'divider') {
      initialProps.color = '#e2e8f0';
      initialProps.borderWidth = 1;
      initialProps.dividerStyle = 'solid';
    }

    const newWidget: WidgetItem = {
      id,
      type,
      x: finalX,
      y: finalY,
      w,
      h,
      properties: initialProps
    };

    markDirty();
    if (needsNewPage) {
      setPages(prev => [...(prev || []), { id: finalPageId, widgets: [newWidget] }]);
      setActivePageId(finalPageId);
    } else {
      setPages(prev => (prev || []).map(page =>
        page && page.id === finalPageId
          ? { ...page, widgets: [...(page.widgets || []), newWidget] }
          : page
      ));
      setActivePageId(finalPageId);
    }
    setSelectedId(id);
  };

  const handleSelectPage = (pageId: string) => {
    setActivePageId(pageId);
    setSelectedId(null);
    setTimeout(() => {
      const el = document.getElementById(`report-page-${pageId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
  };

  const handleAddPage = () => {
    const newPageId = `page-${Date.now()}`;
    const existingHeader = (pages || []).flatMap(p => p?.widgets || []).find(w => w && w.type === 'header');
    const existingFooter = (pages || []).flatMap(p => p?.widgets || []).find(w => w && w.type === 'footer');
    const newWidgets: WidgetItem[] = [];
    if (existingHeader) {
      newWidgets.push({
        id: `header-${Date.now()}`,
        type: 'header',
        x: 0,
        y: 0,
        w: 12,
        h: 1,
        properties: JSON.parse(JSON.stringify(existingHeader.properties || {}))
      });
    }
    if (existingFooter) {
      newWidgets.push({
        id: `footer-${Date.now()}`,
        type: 'footer',
        x: 0,
        y: 24,
        w: 12,
        h: 1,
        properties: JSON.parse(JSON.stringify(existingFooter.properties || {}))
      });
    }
    markDirty();
    const updatedPages = [...(pages || []), { id: newPageId, widgets: newWidgets }];
    setPages(updatedPages);
    setActivePageId(newPageId);
    showSuccess(`Added Page ${updatedPages.length}`);
    setTimeout(() => {
      document.getElementById(`report-page-${newPageId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const handleDeletePage = (pageId: string) => {
    if ((pages || []).length <= 1) {
      showError('Report must contain at least one page');
      return;
    }
    const pageIndex = (pages || []).findIndex(p => p.id === pageId);
    const targetPage = (pages || []).find(p => p.id === pageId);
    if (targetPage && Array.isArray(targetPage.widgets)) {
      const widgetIdsToDelete = targetPage.widgets
        .map(w => w && String(w.id))
        .filter(wId => isIntegerId(wId)) as string[];
      if (widgetIdsToDelete.length > 0) {
        setDeletedWidgetIds(prev => [...prev, ...widgetIdsToDelete]);
      }
    }
    markDirty();
    const newPages = (pages || []).filter(p => p.id !== pageId);
    setPages(newPages);
    if (activePageId === pageId) {
      const nextActiveIndex = Math.min(pageIndex, newPages.length - 1);
      setActivePageId(newPages[nextActiveIndex].id);
    }
    showSuccess(`Deleted Page ${pageIndex + 1}`);
  };

  const handleMovePage = (pageId: string, direction: 'up' | 'down') => {
    const index = (pages || []).findIndex(p => p.id === pageId);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === (pages || []).length - 1) return;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const newPages = [...pages];
    const [movedPage] = newPages.splice(index, 1);
    newPages.splice(targetIndex, 0, movedPage);
    markDirty();
    setPages(newPages);
    setActivePageId(pageId);
    setTimeout(() => {
      document.getElementById(`report-page-${pageId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const handleReorderPages = (sourceIndex: number, destinationIndex: number) => {
    if (sourceIndex === destinationIndex || sourceIndex < 0 || destinationIndex < 0) return;
    if (sourceIndex >= (pages || []).length || destinationIndex >= (pages || []).length) return;
    const newPages = [...pages];
    const [movedPage] = newPages.splice(sourceIndex, 1);
    newPages.splice(destinationIndex, 0, movedPage);
    markDirty();
    setPages(newPages);
    setActivePageId(movedPage.id);
    setTimeout(() => {
      document.getElementById(`report-page-${movedPage.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  // const handleTogglePinPage = (pageId: string, pinType: 'front' | 'back' | 'none') => {
  //   setPages(prev => (prev || []).map(p => p.id === pageId ? { ...p, pinned: pinType } : p));
  //   if (pinType === 'front') {
  //     showSuccess('Page pinned to Front (Cover)');
  //   } else if (pinType === 'back') {
  //     showSuccess('Page pinned to Back (Appendix)');
  //   } else {
  //     showSuccess('Page set to Standard (Flowing)');
  //   }
  // };

  const handleUpdateGlobalSettings = (updates: Partial<ReportGlobalSettings>) => {
    markDirty();
    const nextSettings: ReportGlobalSettings = {
      ...globalSettings,
      ...updates,
      headerProperties: updates.headerProperties
        ? { ...globalSettings.headerProperties, ...updates.headerProperties }
        : globalSettings.headerProperties,
      footerProperties: updates.footerProperties
        ? { ...globalSettings.footerProperties, ...updates.footerProperties }
        : globalSettings.footerProperties,
      watermarkSettings: updates.watermarkSettings
        ? { ...globalSettings.watermarkSettings, ...updates.watermarkSettings }
        : globalSettings.watermarkSettings,
      pageFormat: 'A4'
    };
    setGlobalSettings(nextSettings);

    let updatedPages = pages || [];
    if (updates.headerProperties) {
      updatedPages = updatedPages.map(page => ({
        ...page,
        widgets: (page.widgets || []).map(w => w && w.type === 'header' ? {
          ...w,
          properties: { ...w.properties, ...updates.headerProperties }
        } : w)
      }));
    }

    if (updates.footerProperties) {
      updatedPages = updatedPages.map(page => ({
        ...page,
        widgets: (page.widgets || []).map(w => w && w.type === 'footer' ? {
          ...w,
          properties: { ...w.properties, ...updates.footerProperties }
        } : w)
      }));
    }

    setPages(updatedPages);
  };

  const handleSyncHeaderToAll = () => {
    markDirty();
    const updatedPages = (pages || []).map(page => {
      const existing = (page.widgets || []).find(w => w && w.type === 'header');
      if (existing) {
        return {
          ...page,
          widgets: (page.widgets || []).map(w => w && w.type === 'header' ? {
            ...w,
            properties: { ...w.properties, ...globalSettings.headerProperties }
          } : w)
        };
      } else {
        return {
          ...page,
          widgets: [{
            id: `header-${Date.now()}-${page.id}`,
            type: 'header' as const,
            x: 0,
            y: 0,
            w: 12,
            h: 1,
            properties: { ...globalSettings.headerProperties }
          }, ...(page.widgets || [])]
        };
      }
    });
    setPages(updatedPages);
    showSuccess('Header synced to all pages');
  };

  const handleSyncFooterToAll = () => {
    markDirty();
    const updatedPages = (pages || []).map(page => {
      const existing = (page.widgets || []).find(w => w && w.type === 'footer');
      if (existing) {
        return {
          ...page,
          widgets: (page.widgets || []).map(w => w && w.type === 'footer' ? {
            ...w,
            properties: { ...w.properties, ...globalSettings.footerProperties }
          } : w)
        };
      } else {
        return {
          ...page,
          widgets: [...(page.widgets || []), {
            id: `footer-${Date.now()}-${page.id}`,
            type: 'footer' as const,
            x: 0,
            y: 24,
            w: 12,
            h: 1,
            properties: { ...globalSettings.footerProperties }
          }]
        };
      }
    });
    setPages(updatedPages);
    showSuccess('Footer synced to all pages');
  };

  const selectedWidget = (pages || []).flatMap(p => p?.widgets || []).find(w => w && w.id === selectedId) || null;
  const editingWidget = (pages || []).flatMap(p => p?.widgets || []).find(w => w && w.id === editingWidgetId) || null;

  return (
    <>
      <AddWidgetModal
        isOpen={isChartModalOpen}
        onClose={() => {
          setIsChartModalOpen(false);
          setEditingWidgetId(null);
        }}
        onSubmit={handleAddWidgetFromModal}
        initialData={editingWidget?.properties}
      />

      {/* Centered Clean Confirmation Modal (for Leave & Refresh) */}
      <Modal
        open={confirmModalType !== null}
        onClose={handleCancelConfirmModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 200,
            sx: {
              bgcolor: 'rgba(0, 0, 0, 0.75)',
              backdropFilter: 'blur(6px)'
            }
          }
        }}
      >
        <Fade in={confirmModalType !== null}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90%',
              maxWidth: 420,
              bgcolor: '#111827',
              border: '1px solid #1e293b',
              borderRadius: 3,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
              p: 3,
              outline: 'none'
            }}
          >
            {/* Icon & Close Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  bgcolor: 'rgba(245, 158, 11, 0.12)',
                  border: '1px solid rgba(245, 158, 11, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <AlertTriangle size={22} color="#f59e0b" />
              </Box>
              <IconButton
                size="small"
                onClick={handleCancelConfirmModal}
                sx={{ color: '#94a3b8', '&:hover': { color: '#ffffff', bgcolor: 'rgba(255, 255, 255, 0.08)' } }}
              >
                <X size={16} />
              </IconButton>
            </Box>

            {/* Title & Warning Text */}
            <Typography sx={{ fontSize: 16.5, fontWeight: 700, color: '#f8fafc', mb: 1 }}>
              {confirmModalType === 'leave' ? 'Exit Report Builder?' : 'Refresh Report Builder?'}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: '#94a3b8', lineHeight: 1.6, mb: 3 }}>
              {confirmModalType === 'leave'
                ? 'Your saved report progress is safely stored. Any unsaved modifications made since your last save will not be kept if you leave without saving.'
                : 'Your saved report progress is safely stored. Any unsaved modifications made since your last save will be lost upon reload.'}
            </Typography>

            {/* Action Buttons */}
            {confirmModalType === 'leave' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={isSaving}
                  onClick={handleSaveAndLeave}
                  sx={{
                    height: 36,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'none',
                    bgcolor: '#10b981',
                    color: '#ffffff',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
                    '&:hover': {
                      bgcolor: '#059669'
                    }
                  }}
                >
                  {isSaving ? 'Saving...' : 'Save & Exit'}
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Button
                    fullWidth
                    onClick={handleCancelConfirmModal}
                    sx={{
                      height: 36,
                      fontSize: 12,
                      fontWeight: 600,
                      textTransform: 'none',
                      color: '#cbd5e1',
                      bgcolor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: '#334155',
                        color: '#ffffff'
                      }
                    }}
                  >
                    Keep Editing
                  </Button>
                  <Button
                    fullWidth
                    onClick={handleConfirmDiscardAndLeave}
                    sx={{
                      height: 36,
                      fontSize: 12,
                      fontWeight: 600,
                      textTransform: 'none',
                      color: '#f87171',
                      bgcolor: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.25)',
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: 'rgba(239, 68, 68, 0.2)',
                        color: '#fca5a5'
                      }
                    }}
                  >
                    Exit to Reports
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Button
                  fullWidth
                  onClick={handleCancelConfirmModal}
                  sx={{
                    height: 36,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#cbd5e1',
                    bgcolor: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: '#334155',
                      color: '#ffffff'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleConfirmDiscardAndRefresh}
                  sx={{
                    height: 36,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'none',
                    bgcolor: '#f59e0b',
                    color: '#ffffff',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
                    '&:hover': {
                      bgcolor: '#d97706'
                    }
                  }}
                >
                  Refresh Page
                </Button>
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>
      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden', bgcolor: '#0f172a' }}>
        {/* Canva-Style Left Navigation Dock & Collapsible Panel */}
        <CanvaSidebar
          activeTab={activeSidebarTab}
          onTabChange={setActiveSidebarTab}
          selectedWidget={selectedWidget}
          globalSettings={globalSettings}
          pages={pages}
          activePageId={activePageId}
          onSelectPage={handleSelectPage}
          onAddPage={handleAddPage}
          onDeletePage={handleDeletePage}
          onMovePage={handleMovePage}
          onReorderPages={handleReorderPages}
          onAddWidget={handleAddWidget}
          onUpdateGlobalSettings={handleUpdateGlobalSettings}
          onSaveGlobalSettings={handleSaveCurrentReport}
          onSyncHeaderToAll={handleSyncHeaderToAll}
          onSyncFooterToAll={handleSyncFooterToAll}
          onUpdateWidget={handleUpdateWidget}
          onDeleteWidget={handleDeleteWidget}
          onDeselectWidget={() => setSelectedId(null)}
          onOpenChartModal={handleOpenChartModal}
        />

        {/* Main Canvas Area (Full remaining width!) */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
          {/* Top Editor Toolbar */}
          <TopToolbar
            selectedWidget={selectedWidget}
            globalSettings={globalSettings}
            onBackToReports={handleBackToReports}
            onSaveReport={handleSaveCurrentReport}
            onRefresh={handleRefresh}
            isSaving={isSaving}
          />

          {/* Canvas Area */}
          <ReportCanvas
            pages={pages}
            activePageId={activePageId}
            selectedId={selectedId}
            globalSettings={globalSettings}
            activeReportId={activeReportId}
            onSelectWidget={(selectedWidgetId, pageId) => {
              setSelectedId(selectedWidgetId);
              if (pageId) setActivePageId(pageId);
              if (selectedWidgetId) {
                setIsRightPropsOpen(true);
              }
            }}
            onLayoutChange={handleLayoutChange}
            onAddPage={handleAddPage}
            onDuplicateWidget={handleDuplicateWidget}
            onDeleteWidget={handleDeleteWidget}
            onEditWidget={handleEditWidget}
            onDropWidget={handleDropWidget}
            onUpdateWidget={handleUpdateWidget}
            onMoveWidgetToPage={handleMoveWidgetToPage}
          />
        </Box>

        {/* Right Props Inspector Panel */}
        <RightPropsPanel
          selectedWidget={selectedWidget}
          onUpdateWidget={handleUpdateWidget}
          onDeleteWidget={handleDeleteWidget}
          onAddWidget={handleAddWidget}
          onSyncHeaderToAll={handleSyncHeaderToAll}
          onSyncFooterToAll={handleSyncFooterToAll}
          onOpenChartModal={handleOpenChartModal}
          activeReportId={activeReportId}
          isOpen={isRightPropsOpen}
          onClose={() => setIsRightPropsOpen(false)}
        />
      </Box>
    </>
  );
};

export default CustomReportsMain;
